import { getClassNames } from '@ab-core/functions/styles/classNameFormatter';
import { Icon } from '@ab-core/icon';
import type { WithTestId } from '@ab-core/testing';
import { getTestIdProp } from '@ab-core/testing';
import type { ReactNode } from 'react';
import React, { useEffect, useState } from 'react';
import { Checkbox, CheckboxContainer, Label, LabelContainer } from './styled';

export enum BASIC_CHECKBOX_SIZE {
    NORMAL = 'normal',
    BASIC = 'basic'
}

export type BasicCheckboxProps = Omit<React.ComponentPropsWithoutRef<'input'>, 'type'> &
    WithTestId & {
        label?: ReactNode;
        inputSize?: BASIC_CHECKBOX_SIZE;
        error?: boolean;
    };

export const BasicCheckbox = React.forwardRef<HTMLInputElement, BasicCheckboxProps>((props, ref) => {
    const {
        label,
        error = false,
        inputSize = BASIC_CHECKBOX_SIZE.NORMAL,
        checked,
        onChange,
        disabled,
        className,
        id,
        ...rest
    } = props;

    const [isChecked, setIsChecked] = useState(checked || false);

    useEffect(() => {
        setIsChecked(checked || false);
    }, [checked]);

    const onChangeTrigger: BasicCheckboxProps['onChange'] = (e) => {
        if (onChange) {
            onChange(e);
        }

        setIsChecked(e.target.checked);
    };

    const labelClass = getClassNames({
        className,
        prefix: 'checkbox-label',
        modifier: { size: inputSize, disabled }
    });
    const checkboxContainerClass = getClassNames({
        prefix: 'checkbox-container',
        modifier: { error, disabled, checked: isChecked }
    });
    const labelContainerClass = getClassNames({
        prefix: 'checkbox-label-container',
        modifier: { error }
    });

    return (
        <Label className={labelClass}>
            <CheckboxContainer className={checkboxContainerClass}>
                <Checkbox
                    {...rest}
                    ref={ref}
                    type="checkbox"
                    checked={isChecked}
                    onChange={onChangeTrigger}
                    disabled={disabled}
                />
                <Icon name="Checkmark" size="SMALL_4" color="white" />
            </CheckboxContainer>
            {label && (
                <LabelContainer className={labelContainerClass} {...getTestIdProp(id)}>
                    {label}
                </LabelContainer>
            )}
        </Label>
    );
});
